'use client';

import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import {
  Award,
  Users,
  Star,
  CheckCircle,
  Hospital,
  BrainCircuit,
  Globe,
  TrendingUp,
  Wrench,
  Microscope,
  ArrowRight,
} from "lucide-react";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/components/ui/tooltip";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import { DrawSVGPlugin } from "gsap/DrawSVGPlugin";
import { useGSAP } from '@gsap/react';
import SectionTitle from '@/components/home/section-title';
import Breadcrumbs from '@/components/Breadcrumbs';
import OptimizedImage from '@/components/optimized-media/OptimizedImage';
import { useTranslation } from '@/hooks/useTranslation';
import { useParams } from 'next/navigation';
import { isRTLLanguage } from '@/lib/i18n/config';

gsap.registerPlugin(ScrollTrigger, MotionPathPlugin, DrawSVGPlugin);

// Component for infinite scrolling logos
function InfiniteLogoScroll() {
  const logos = [
    { src: "/images/logos/emax-logo.webp", alt: "E-max", width: 942, height: 265 },
    { src: "/images/logos/Dentsply_sirona_logo.webp", alt: "Dentsply Sirona", width: 2560, height: 763 },
    { src: "/images/logos/3M.webp", alt: "3M", width: 1280, height: 673 },
    { src: "/images/logos/3shape-logo.webp", alt: "3Shape", width: 500, height: 210 },
    { src: "/images/logos/ivoclar.webp", alt: "Ivoclar", width: 9449, height: 2326 },
    { src: "/images/logos/logo-straumann.webp", alt: "Straumann", width: 622, height: 216 },
  ];

  useEffect(() => {
    // Add keyframes for the scroll animation
    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
      @keyframes scroll {
        0% { transform: translateX(0); }
        100% { transform: translateX(calc(-180px * 6 - 24px * 6)); }
      }
    `;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  return (
    <div className="marquee-container overflow-hidden relative py-4">
      {/* First set of logos */}
      <div
        className="flex gap-4"
        style={{
          whiteSpace: "nowrap",
          animation: "scroll 25s linear infinite",
          width: "fit-content",
          padding: "4px 30px",
        }}
      >
        {[...Array(3)].map((_, groupIndex) =>
          logos.map((logo, index) => (
            <div
              key={`logo-${groupIndex}-${index}`}
              className="logo-item inline-flex h-20 w-[150px] bg-white rounded-lg shadow-md p-3 items-center justify-center"
              style={{
                margin: "0 8px",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-3px)";
                e.currentTarget.style.boxShadow =
                  "0 8px 20px rgba(0, 0, 0, 0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "";
              }}
            >
              <OptimizedImage
                src={logo.src}
                alt={logo.alt}
                width={logo.width}
                height={logo.height}
                fill={false}
                className="max-h-14 max-w-full object-contain"
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// AnimatedCounter: animates numbers from 0 to value
function AnimatedCounter({ value, delay = 0, locale }: { value: number; delay?: number; locale: string }) {
  const [count, setCount] = useState(0);
  const counterRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Reset and start animation when visible
            setCount(0);
            const start = 0;
            const end = value;
            if (start === end) return;

            // Add delay before starting the counter animation
            setTimeout(() => {
              const incrementTime = 20;
              let current = start;
              const step = Math.ceil(end / 50);

              const timer = setInterval(() => {
                current += step;
                if (current >= end) {
                  setCount(end);
                  clearInterval(timer);
                } else {
                  setCount(current);
                }
              }, incrementTime);
            }, delay);
          }
        });
      },
      {
        threshold: 0.5,
        rootMargin: '0px'
      }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => {
      if (counterRef.current) {
        observer.unobserve(counterRef.current);
      }
    };
  }, [value, delay]);

  // Format number based on locale
  // For Arabic, use Arabic-Indic numerals without separator for cleaner look
  // For Persian, use Persian numerals without separator
  let formattedCount;
  if (locale === 'ar') {
    // Arabic uses Eastern Arabic numerals
    formattedCount = count.toLocaleString('ar-EG', { useGrouping: false });
  } else if (locale === 'fa') {
    // Persian uses Persian numerals
    formattedCount = count.toLocaleString('fa-IR', { useGrouping: false });
  } else {
    // Other locales use standard formatting with grouping
    formattedCount = count.toLocaleString(locale);
  }

  return <span ref={counterRef}>{formattedCount}</span>;
}

// TechTabsSection component
function TechTabsSection({ t, isRTL }: { t: (key: string) => any, isRTL: boolean }) {
  const getBullets = (key: string) => {
    // Direct fallback to individual bullet translation keys to avoid array translation warnings
    if (key.includes('scanning')) {
      return [
        t('technologies.items.scanning.bullets.0') || "Highly accurate digital impressions",
        t('technologies.items.scanning.bullets.1') || "Comfortable, quick scanning process", 
        t('technologies.items.scanning.bullets.2') || "Immediate visualization of your oral situation",
        t('technologies.items.scanning.bullets.3') || "Seamless integration with our digital workflow"
      ].filter(Boolean);
    } else if (key.includes('smileDesign')) {
      return [
        t('technologies.items.smileDesign.bullets.0') || "Personalized smile simulations",
        t('technologies.items.smileDesign.bullets.1') || "Collaborative treatment planning",
        t('technologies.items.smileDesign.bullets.2') || "Predictable, beautiful outcomes"
      ].filter(Boolean);
    } else if (key.includes('dentalLab')) {
      return [
        t('technologies.items.dentalLab.bullets.0') || "Same-day crowns and bridges",
        t('technologies.items.dentalLab.bullets.1') || "Custom shade matching", 
        t('technologies.items.dentalLab.bullets.2') || "Rigorous quality assurance"
      ].filter(Boolean);
    } else if (key.includes('xray')) {
      return [
        t('technologies.items.xray.bullets.0') || "Minimal radiation exposure",
        t('technologies.items.xray.bullets.1') || "Instant image availability",
        t('technologies.items.xray.bullets.2') || "Enhanced diagnostic accuracy"
      ].filter(Boolean);
    }
    return [];
  };

  const techs = [
    {
      title: t('technologies.items.scanning.title'),
      subtitle: t('technologies.items.scanning.subtitle'),
      description: t('technologies.items.scanning.description'),
      bullets: getBullets('technologies.items.scanning.bullets'),
      image: "/images/treatments/3dscan.webp", width: 800, height: 600
    },
    {
      title: t('technologies.items.smileDesign.title'),
      subtitle: t('technologies.items.smileDesign.subtitle'),
      description: t('technologies.items.smileDesign.description'),
      bullets: getBullets('technologies.items.smileDesign.bullets'),
      image: "/images/treatments/hollywood-smile.webp", width: 1000, height: 667
    },
    {
      title: t('technologies.items.dentalLab.title'),
      subtitle: t('technologies.items.dentalLab.subtitle'),
      description: t('technologies.items.dentalLab.description'),
      bullets: getBullets('technologies.items.dentalLab.bullets'),
      image: "/images/env/dental-lab.webp", width: 1024, height: 671
    },
    {
      title: t('technologies.items.xray.title'),
      subtitle: t('technologies.items.xray.subtitle'),
      description: t('technologies.items.xray.description'),
      bullets: getBullets('technologies.items.xray.bullets'),
      image: "/images/treatments/digital-xray.webp", width: 1200, height: 800
    },
  ];
  const [selected, setSelected] = useState(0);

  return (
    <div className="bg-muted/30 dark:bg-gray-800/50 rounded-xl p-4">
      {/* Tab Bar */}
      <div className="flex flex-wrap gap-2 justify-center mb-8">
        {techs.map((tech, i) => (
          <motion.button
            key={i}
            className={`px-6 py-2 rounded-lg font-semibold transition-colors whitespace-nowrap border focus:outline-hidden
              ${selected === i
                ? "bg-white dark:bg-gray-700 text-primary border-primary shadow-sm"
                : "bg-muted dark:bg-gray-800 text-muted-foreground dark:text-gray-300 border-transparent hover:bg-white/80 dark:hover:bg-gray-700/80"
              }`}
            onClick={() => setSelected(i)}
            aria-selected={selected === i}
            tabIndex={0}
            whileHover={{
              scale: 1.06,
              boxShadow: "0 2px 12px rgba(80,80,200,0.08)",
            }}
            whileTap={{ scale: 0.97 }}
          >
            {tech.title}
          </motion.button>
        ))}
      </div>
      {/* Two-column layout with animated content and fixed min-h to prevent jump */}
      <motion.div
        layout
        className="flex flex-col md:flex-row gap-8 items-stretch min-h-[420px] relative"
      >
        {/* Left: Card with text */}
        <div className="flex-1">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={selected}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.35, ease: "easeInOut" }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-8 h-full flex flex-col justify-center"
              layout
            >
              <h3 className={`text-2xl font-bold mb-2 text-gray-900 dark:text-gray-100 ${isRTL ? 'text-right' : 'text-left'}`}>
                {techs[selected].title}
              </h3>
              <p className={`text-muted-foreground dark:text-gray-400 mb-4 text-base font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                {techs[selected].subtitle}
              </p>
              <p className={`mb-4 text-gray-700 dark:text-gray-300 ${isRTL ? 'text-right' : 'text-left'}`}>
                {techs[selected].description}
              </p>
              <ul className={`list-disc ${isRTL ? 'pr-5' : 'pl-5'} space-y-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                {techs[selected].bullets.map((b: string, i: number) => (
                  <li key={i} className="text-primary dark:text-white">
                    {b}
                  </li>
                ))}
              </ul>
            </motion.div>
          </AnimatePresence>
        </div>
        {/* Right: Image with animation */}
        <div className="flex-1 flex items-center justify-center">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={techs[selected].image}
              className="w-full max-w-md h-80 rounded-xl bg-muted shadow-lg overflow-hidden relative"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.35, ease: "easeInOut" }}
              whileHover={{ scale: 1.03 }}
            >
              <OptimizedImage
                src={techs[selected].image}
                alt={techs[selected].title}
                fill={true}
                style={{ objectFit: "cover" }}
              />
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}

// JourneyTimeline component
function JourneyTimeline({ t }: { t: (key: string) => string }) {
  const milestones = [
    {
      icon: <CheckCircle className="w-7 h-7 text-navy" />,
      text: t('journey.milestones.founded'),
    },
    {
      icon: <Hospital className="w-7 h-7 text-yellow-500" />,
      text: t('journey.milestones.facility'),
    },
    {
      icon: <BrainCircuit className="w-7 h-7 text-navy" />,
      text: t('journey.milestones.team'),
    },
    {
      icon: <Microscope className="w-7 h-7 text-yellow-500" />,
      text: t('journey.milestones.lab'),
    },
    {
      icon: <Globe className="w-7 h-7 text-navy" />,
      text: t('journey.milestones.international'),
    },
    {
      icon: <Award className="w-7 h-7 text-yellow-500" />,
      text: t('journey.milestones.awards'),
    },
    {
      icon: <TrendingUp className="w-7 h-7 text-navy" />,
      text: t('journey.milestones.smiles'),
    },
    {
      icon: <Wrench className="w-7 h-7 text-yellow-500" />,
      text: t('journey.milestones.protocols'),
    },
    {
      icon: <ArrowRight className="w-7 h-7 text-navy animate-pulse" />,
      text: t('journey.milestones.future'),
    },
  ];
  const milestonesListRef = useRef<HTMLOListElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const chevronRef = useRef<SVGSVGElement>(null);
  const milestoneRefs = useRef<(HTMLLIElement | null)[]>([]);
  const [lineHeight, setLineHeight] = useState(0);

  useEffect(() => {
    // Wait for DOM to render and measure height
    if (milestonesListRef.current) {
      setLineHeight(milestonesListRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    if (
      !lineRef.current ||
      !chevronRef.current ||
      !milestoneRefs.current.length ||
      !lineHeight
    )
      return;
    const scaleY = { value: 0 };
    const line = lineRef.current;
    const chevron = chevronRef.current;
    gsap.fromTo(
      scaleY,
      { value: 0 },
      {
        value: 1,
        ease: "none",
        scrollTrigger: {
          trigger: milestonesListRef.current,
          start: "top 85%",
          endTrigger: milestoneRefs.current[milestones.length - 1],
          end: "top 60%",
          scrub: 1.5,
        },
        onUpdate: () => {
          line.style.transform = `scaleY(${scaleY.value})`;
          const currentLineHeight = line.offsetHeight;
          chevron.style.transform = `translate(-50%, ${scaleY.value * currentLineHeight - 10
            }px)`;
          chevron.style.opacity = "1";
        },
      }
    );
    // Milestone fade/slide in
    milestoneRefs.current.forEach((el) => {
      if (!el) return;
      gsap.fromTo(
        el,
        { opacity: 0, x: -32 },
        {
          opacity: 1,
          x: 0,
          ease: "power2.out",
          scrollTrigger: {
            trigger: el,
            start: "top 85%",
            end: "top 60%",
            scrub: 1.5,
            toggleActions: "play none none reverse",
          },
        }
      );
    });
  }, [lineHeight, milestones.length]);

  return (
    <div className="max-w-3xl mx-auto relative flex">
      {/* Timeline line and chevron, absolutely positioned */}
      <div
        className="absolute left-2 top-0 z-0"
        style={{
          width: "28px",
          height: lineHeight || undefined,
          pointerEvents: "none",
          position: "absolute",
        }}
      >
        <div
          ref={lineRef}
          className="w-1 rounded-full"
          style={{
            height: lineHeight || 0,
            background: "linear-gradient(to bottom, var(--color-gold), var(--color-navy))",
            transform: "scaleY(0)",
            transformOrigin: "top",
            position: "absolute",
            left: "50%",
            top: 0,
            translate: "-50% 0",
          }}
        />
        <svg
          ref={chevronRef}
          className="w-7 h-7 absolute left-1/2 text-navy"
          style={{ top: 0, transform: "translate(-50%, 0)" }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
      <ol
        ref={milestonesListRef}
        className="relative pl-12 space-y-8 z-10 w-full"
      >
        {milestones.map((milestone, i) => (
          <li
            key={i}
            ref={(el) => {
              milestoneRefs.current[i] = el;
            }}
            className="flex items-start gap-4 opacity-0"
          >
            <span className="bg-primary/10 rounded-full p-2 mb-1 flex items-center justify-center w-10 h-10">
              {milestone.icon}
            </span>
            <p className="text-muted-foreground text-base">{milestone.text}</p>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function About() {
  const { t } = useTranslation('about' as any);
  const params = useParams();
  const locale = params.locale as string;
  const isRTL = isRTLLanguage(locale);
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  const pageRef = useRef<HTMLDivElement>(null);

  // Add ScrollTrigger animations for all sections
  useEffect(() => {
    if (sectionRefs.current.length === 0) return;

    // Create animations for each section
    sectionRefs.current.forEach((section) => {
      if (!section) return;

      // Create a timeline for this section
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top 80%",
          end: "top 40%",
          scrub: 0.5,
          toggleActions: "play none none reverse",
        },
      });

      // Find child elements to animate
      const content = section.querySelector(
        ".container > div:not(.section-title-wrapper)"
      );

      if (content) {
        tl.fromTo(
          content,
          { y: 50, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.6, ease: "power2.out" },
          0.2
        );
      }
    });

    // Special animation for the About DentaWista section
    const aboutImage = document.getElementById("about-image");
    const aboutText = document.getElementById("about-text");
    const aboutP1 = document.getElementById("about-p1");
    const aboutP2 = document.getElementById("about-p2");
    const aboutP3 = document.getElementById("about-p3");
    const aboutMotto = document.getElementById("about-motto");

    if (aboutImage && aboutText) {
      const aboutTl = gsap.timeline({
        scrollTrigger: {
          trigger: "#about-text",
          start: "top 75%",
          end: "top 25%",
          scrub: 0.5,
          toggleActions: "play none none reverse",
        },
      });

      aboutTl
        .fromTo(
          aboutText,
          { x: -100, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.6, ease: "power2.out" },
          0
        )
        .fromTo(
          aboutImage,
          { x: 100, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.6, ease: "power2.out" },
          0.4
        );

      // Staggered paragraph animations
      if (aboutP1 && aboutP2 && aboutP3 && aboutMotto) {
        aboutTl
          .fromTo(
            [aboutP1, aboutP2, aboutP3],
            { y: 30, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              stagger: 0.15,
              duration: 0.5,
              ease: "power1.out",
            },
            0.3
          )
          .fromTo(
            aboutMotto,
            { y: 20, opacity: 0, scale: 0.95 },
            {
              y: 0,
              opacity: 1,
              scale: 1,
              duration: 0.4,
              ease: "back.out(1.5)",
            },
            0.7
          );
      }
    }

    // Special animation for the team section
    const teamImage = document.getElementById("team-image");
    const teamText = document.getElementById("team-text");
    const teamQuote = document.getElementById("team-quote");

    if (teamImage && teamText && teamQuote) {
      const teamTl = gsap.timeline({
        scrollTrigger: {
          trigger: "#about-team",
          start: "top 70%",
          end: "top 30%",
          scrub: 0.5,
          toggleActions: "play none none reverse",
        },
      });

      teamTl
        .fromTo(
          teamImage,
          { x: -100, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.6, ease: "power2.out" },
          0
        )
        .fromTo(
          teamText,
          { x: 100, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.6, ease: "power2.out" },
          0.2
        )
        .fromTo(
          teamQuote,
          { y: 20, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.4, ease: "power2.out" },
          0.4
        );
    }

    // Special animation for the World-Class Tools section
    const toolsSection = document.querySelector("#tools-section");
    const toolsContent = document.querySelector("#tools-content");
    const toolsLogos = document.querySelector("#tools-logos");

    if (toolsSection && toolsContent && toolsLogos) {
      // Set initial state
      gsap.set([toolsContent, toolsLogos], { opacity: 0, y: 50 });

      const toolsTl = gsap.timeline({
        scrollTrigger: {
          trigger: toolsSection,
          start: "top 70%",
          end: "top 30%",
          scrub: 0.5,
          toggleActions: "play none none reverse",
        },
      });

      toolsTl
        .to(toolsContent, {
          y: 0,
          opacity: 1,
          duration: 0.6,
          ease: "power2.out",
        })
        .to(toolsLogos, {
          y: 0,
          opacity: 1,
          duration: 0.6,
          ease: "power2.out",
        }, "-=0.3");
    }

    // Special animation for the Technology section
    const techSection = document.querySelector("#tech-section");
    const techContent = document.querySelector("#tech-content");

    if (techSection && techContent) {
      // Set initial state
      gsap.set(techContent, { opacity: 0, y: 50 });

      const techTl = gsap.timeline({
        scrollTrigger: {
          trigger: techSection,
          start: "top 70%",
          end: "top 30%",
          scrub: 0.5,
          toggleActions: "play none none reverse",
        },
      });

      techTl.to(techContent, {
        y: 0,
        opacity: 1,
        duration: 0.6,
        ease: "power2.out",
      });
    }

    // Special animation for the Recognized Excellence section
    const excellenceSection = document.querySelector("#excellence-section");
    const excellenceContent = document.querySelector("#excellence-content");
    const excellenceLogos = document.querySelectorAll("#excellence-logos .bg-white\\/90");

    if (excellenceSection && excellenceContent && excellenceLogos.length) {
      // Set initial states
      gsap.set(excellenceContent, { opacity: 0, y: 30 });
      gsap.set(excellenceLogos, { opacity: 0, y: 20, scale: 0.9 });

      const excellenceTl = gsap.timeline({
        scrollTrigger: {
          trigger: excellenceSection,
          start: "top 85%",
          end: "top 35%",
          scrub: 0.5,
          toggleActions: "play none none reverse",
        },
      });

      excellenceTl
        .to(excellenceContent, {
          y: 0,
          opacity: 1,
          duration: 0.6,
          ease: "power2.out",
        })
        .to(excellenceLogos, {
          y: 0,
          opacity: 1,
          scale: 1,
          stagger: 0.1,
          duration: 0.4,
          ease: "back.out(1.7)",
        }, "-=0.2");
    }

    // Refresh ScrollTrigger after animations are set up
    ScrollTrigger.refresh();

    // Clean up all animations on unmount
    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <div
      ref={pageRef}
      className="flex flex-col min-h-screen bg-background overflow-x-hidden"
    >
      {/* Optional: Global ScrollSmoother for the entire page */}
      {/* <ScrollSmoother /> */}

      {/* Hero Section - Modernized & Taller */}
      <section
        className="relative py-52 text-center bg-gray-900 overflow-hidden"
        ref={(el) => {
          sectionRefs.current[0] = el;
        }}
      >
        {/* Ken Burns Background Image */}
        <div className="absolute inset-0 z-0">
          <OptimizedImage
            src="/images/env/env.webp"
            alt="DentaWista Clinic Environment"
            fill={true}
            style={{ objectFit: "cover" }}
            className="object-center scale-100 animate-kenburns"
            priority
          />
          {/* Animated Gradient Overlay */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-br from-primary/60 via-black/60 to-accent/60 mix-blend-multiply"
            initial={{ opacity: 0.7 }}
            animate={{ opacity: [0.7, 0.9, 0.7] }}
            transition={{
              duration: 6,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          />
        </div>
        {/* Content Container */}
        <div className="relative container mx-auto px-4 z-10">
          <motion.h1
            className="text-5xl md:text-6xl font-bold mb-6 text-white [text-shadow:0_2px_4px_rgba(0,0,0,0.5)] font-display"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
{t('hero.title')}
          </motion.h1>
          <motion.p
            className="text-xl text-slate-200 max-w-3xl mx-auto mt-20 mb-8 [text-shadow:0_1px_3px_rgba(0,0,0,0.4)]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 1 }}
          >
{t('hero.subtitle')}
          </motion.p>
        </div>
      </section>

      {/* About DentaWista Section - Modern, Gradient, Wide, Centered Title with special animation */}
      <section
        className="py-16 bg-gradient-to-br from-accent/10 via-primary/5 to-background"
        ref={(el) => {
          sectionRefs.current[1] = el;
        }}
      >
        <div className="container mx-auto px-4">
          <Breadcrumbs />
          <div className="max-w-7xl mx-auto mb-8 text-center">
            <SectionTitle>{t('about.title')}</SectionTitle>
            <p className="mt-3 text-primary text-lg font-semibold text-center">
              {t('about.subtitle')}
            </p>
          </div>
          <div className={`max-w-7xl mx-auto flex flex-col md:flex-row-reverse items-center gap-16 ${isRTL ? 'text-right' : 'text-left'}`}>
            {/* Image right/top - will slide in from right with GSAP */}
            <div
              className="w-full max-w-xl rounded-xl shadow-lg object-cover mb-6 md:mb-0 md:ml-12 shrink-0 opacity-0"
              id="about-image"
            >
              <OptimizedImage
                src="/images/env/env1.webp"
                alt="DentaWista Clinic Environment"
                width={1665}
                height={1266}
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>
            {/* Text left/below - will slide in from left with GSAP */}
            <div className="flex-1 opacity-0" id="about-text">
              <div className="space-y-6 text-gray-700 dark:text-[#94a3b8] text-lg leading-relaxed">
                <p id="about-p1">
                  {t('about.content.paragraph1')}
                </p>
                <p id="about-p2">
                  {t('about.content.paragraph2')}
                </p>
                <p id="about-p3">
                  {t('about.content.paragraph3')}
                </p>
                <p id="about-motto" className="font-semibold text-primary">
                  {t('about.content.motto')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Modernized */}
      <section
        className="py-16 bg-muted relative overflow-hidden"
        ref={(el) => {
          sectionRefs.current[2] = el;
        }}
      >
        {/* Subtle background gradient shape */}
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] bg-gradient-to-br from-primary/10 to-accent/10 rounded-full blur-3xl opacity-60 pointer-events-none z-0" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(() => {
              const stats = [
                { icon: Users, label: t('stats.patients.label'), value: 10000, iconColor: "text-navy", textColor: "text-navy" },
                { icon: Star, label: t('stats.rating.label'), value: 5, iconColor: "text-yellow-500", textColor: "text-navy", delay: 300, animate: true },
                { icon: Award, label: t('stats.awards.label'), value: 25, iconColor: "text-yellow-500", textColor: "text-navy", delay: 300 },
              ];
              return (isRTL ? [...stats].reverse() : stats);
            })().map((stat, index) => (
              <motion.div
                key={index}
                whileHover={{
                  scale: 1.04,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                }}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: index * 0.2 }}
              >
                <Card className="text-center transition-shadow">
                  <CardContent className="pt-6">
                    <motion.div
                      initial={{ scale: 0.7, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                      className="flex justify-center"
                    >
                      <motion.div
                        animate={stat.animate ? {
                          y: [0, -10, 0],
                          rotate: [0, 360],
                        } : {}}
                        transition={stat.animate ? {
                          y: {
                            duration: 0.5,
                            times: [0, 0.5, 1],
                            ease: "easeInOut",
                            repeat: 0
                          },
                          rotate: {
                            duration: 20,
                            repeat: Infinity,
                            ease: "linear"
                          }
                        } : {}}
                      >
                        <stat.icon className={`w-12 h-12 mx-auto mb-4 ${stat.iconColor}`} />
                      </motion.div>
                    </motion.div>
                    <motion.h3
                      className={`text-3xl font-bold mb-2 ${stat.textColor}`}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ duration: 0.7, delay: 0.3 + index * 0.1 }}
                    >
                      <AnimatedCounter value={stat.value} delay={stat.delay} locale={locale} />
                      {stat.icon === Star ? "" : "+"}
                    </motion.h3>
                    <p className="text-muted-foreground">{stat.label}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* History Section - Milestones List, Animated Arrow Timeline */}
      <section
        className="py-16 bg-background"
        ref={(el) => {
          sectionRefs.current[3] = el;
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <SectionTitle>{t('journey.title')}</SectionTitle>
          <JourneyTimeline t={t} />
        </div>
      </section>

      {/* Team Section - Modernized with new content */}
      <section
        id="about-team"
        className="py-16 bg-muted"
        ref={(el) => {
          sectionRefs.current[4] = el;
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <SectionTitle>{t('team.title')}</SectionTitle>
          <div
            className="flex flex-col md:flex-row gap-12 max-w-6xl mx-auto mt-10"
            id="team-content"
          >
            {/* Image Column - will slide in from left with GSAP */}
            <div className="md:w-2/5 shrink-0 opacity-0" id="team-image">
              <OptimizedImage
                src="/images/env/drs.webp"
                alt="DentaWista Medical Team"
                width={1080}
                height={1080}
                className="w-full h-auto rounded-xl shadow-lg object-cover"
              />
              <div className={`mt-6 ${isRTL ? 'pr-3 border-r-4' : 'pl-3 border-l-4'} border-primary`}>
                <h3 className={`text-xl font-bold text-gray-800 dark:text-white ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('team.heading')}
                </h3>
              </div>
            </div>

            {/* Content Column - will slide in from right with GSAP */}
            <div className="md:w-3/5 opacity-0" id="team-text">
              <div className={`space-y-5 text-muted-foreground dark:text-slate-300 ${isRTL ? 'text-right' : 'text-left'}`}>
                <p className="text-lg">
                  {t('team.content.paragraph1')}
                </p>

                <p className="text-lg">
                  {t('team.content.paragraph2')}
                </p>

                <div
                  className={`py-3 px-5 my-6 bg-primary/10 dark:bg-primary/30 ${isRTL ? 'border-r-4 rounded-l-lg' : 'border-l-4 rounded-r-lg'} border-primary opacity-0`}
                  id="team-quote"
                >
                  <p className={`text-lg font-semibold text-primary dark:text-white ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('team.content.quote')}
                  </p>
                </div>

                <p className="text-lg">
                  {t('team.content.paragraph3')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Materials & Technologies Section with Bold, Modern Background */}
      <section
        id="tools-section"
        className="pt-10 pb-6 relative overflow-hidden bg-white dark:bg-gray-900"
        ref={(el) => {
          sectionRefs.current[5] = el;
        }}
      >
        {/* Ultra-modern, bold background */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          {/* Strong, modern gradient base */}
          <div className="absolute inset-0 bg-gradient-to-br from-white via-gray-50 to-primary/5 dark:from-gray-900 dark:via-gray-900 dark:to-primary/10"></div>

          {/* Bold geometric elements */}
          <div className="absolute -right-20 -top-20 w-[400px] h-[400px] bg-primary/5 rounded-full blur-xl"></div>
          <div className="absolute right-[10%] top-[10%] w-40 h-40 border-8 border-primary/10 rounded-full"></div>
          <div className="absolute left-[5%] bottom-[5%] w-60 h-60 border-16 border-accent/10 rounded-full"></div>

          {/* Bold dental instrument abstract */}
          <div className="absolute left-[30%] bottom-0 w-4 h-[60%] bg-gradient-to-t from-primary/10 to-transparent transform -rotate-12"></div>
          <div className="absolute left-[28%] bottom-0 w-1 h-[70%] bg-gradient-to-t from-primary/15 to-transparent transform -rotate-12"></div>
          <div className="absolute left-[35%] bottom-0 w-8 h-[40%] bg-gradient-to-t from-accent/10 to-transparent transform -rotate-6"></div>

          {/* Bold tooth motif - simplified, modern */}
          <div className="absolute bottom-0 right-[15%] w-40 h-40">
            <svg
              viewBox="0 0 100 100"
              fill="currentColor"
              className="w-full h-full text-primary/10"
            >
              <path d="M50,20 C40,20 30,25 30,40 C30,55 25,65 25,75 C25,85 35,85 40,80 C45,75 50,75 50,75 C50,75 55,75 60,80 C65,85 75,85 75,75 C75,65 70,55 70,40 C70,25 60,20 50,20 Z" />
            </svg>
          </div>

          {/* Dynamic tech-inspired pattern */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `
              radial-gradient(circle at 50% 50%, rgba(255,255,255,0.4) 0%, transparent 5%),
              radial-gradient(circle at 80% 80%, rgba(255,255,255,0.4) 0%, transparent 5%)
            `,
              backgroundSize: "20px 20px, 15px 15px",
            }}
          ></div>

          {/* Bold, angled scan lines */}
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `repeating-linear-gradient(
              -45deg,
              transparent,
              transparent 2px,
              rgba(0, 0, 0, 0.1) 2px,
              rgba(0, 0, 0, 0.1) 4px
            )`,
            }}
          ></div>
        </div>

        <div className="container mx-auto px-4 mb-6 relative z-10 text-center">
          <SectionTitle>{t('tools.title')}</SectionTitle>
          <div id="tools-content">
            <p className="text-center text-muted-foreground dark:text-gray-300 max-w-3xl mx-auto">
              {t('tools.subtitle')}
            </p>
          </div>
        </div>

        {/* Infinite scroll logos container */}
        <div id="tools-logos" className="relative w-full overflow-hidden pt-6 pb-3 px-8 z-10">
          <InfiniteLogoScroll />
        </div>
      </section>

      {/* Technology Section - Tabbed, Sliding Content */}
      <section
        id="tech-section"
        className="py-16 bg-background dark:bg-background/40"
        ref={(el) => {
          sectionRefs.current[6] = el;
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <SectionTitle>{t('technologies.title')}</SectionTitle>
          <div id="tech-content">
            <TechTabsSection t={t} isRTL={isRTL} />
          </div>
        </div>
      </section>

      {/* Recognized Excellence Section */}
      <section
        id="excellence-section"
        className="py-16 bg-muted relative overflow-hidden"
        ref={(el) => {
          sectionRefs.current[7] = el;
        }}
      >
        <div className="container mx-auto px-4 relative z-10">
          <SectionTitle>{t('excellence.title')}</SectionTitle>
          <div id="excellence-content" className="bg-card/80 dark:bg-card/50 p-8 rounded-lg shadow-lg max-w-4xl mx-auto">
            <TooltipProvider>
              <div
                id="excellence-logos"
                className="flex flex-wrap items-center justify-center gap-10 py-2"
              >
                {[
                  {
                    src: "/images/logos/health-logo.webp", alt: "Ministry of Health License", label: t('excellence.certifications.health'), width: 515, height: 515
                  },
                  {
                    src: "/images/logos/ktb.webp", alt: "KTB Certification", label: t('excellence.certifications.tourism'), width: 591, height: 225
                  },
                  {
                    src: "/images/logos/IFIA.webp", alt: "IFIA Membership", label: t('excellence.certifications.ifia'), width: 588, height: 285
                  },
                  {
                    src: "/images/logos/iso.webp", alt: "ISO 9001 Certified", label: t('excellence.certifications.iso'), width: 417, height: 417
                  },
                ].map((logo, index) => (
                  <Tooltip key={index}>
                    <TooltipTrigger asChild>
                      <motion.div
                        className="bg-white/90 dark:bg-gray-700/90 p-2 rounded-lg shadow-md transition-transform cursor-pointer"
                        whileHover={{
                          scale: 1.14,
                          rotate: -3,
                          boxShadow: "0 6px 32px rgba(80,80,200,0.13)",
                        }}
                        whileTap={{ scale: 0.98, rotate: 2 }}
                        transition={{
                          duration: 0.5,
                          type: "spring",
                          stiffness: 180,
                        }}
                      >
                        <div style={{ maxWidth: '120px', maxHeight: '120px', overflow: 'hidden' }}>
                          <OptimizedImage
                            src={logo.src}
                            alt={logo.alt}
                            width={logo.width}
                            height={logo.height}
                            fill={false}
                            className="w-auto object-contain mx-auto"
                          />
                        </div>
                      </motion.div>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      className="transition-all duration-200 will-change-transform"
                    >
                      <motion.span
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.2 }}
                      >
                        {logo.label}
                      </motion.span>
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </TooltipProvider>
          </div>
        </div>
      </section>
    </div>
  );
}
